/**
 * Email Preview Service
 *
 * Pure API functions for email preview feature.
 * Proxies requests through backend API which handles Mailgun authentication.
 *
 * Backend endpoints:
 * - POST /api/v2.0/email/preview - Create preview test
 * - POST /api/v2.0/email/preview/results - Get test results
 */

import { generateLinkViewerUrl } from '@setvi/shared/utils/viewer';
import { getPreviewLink } from '@setvi/shared/utils';
import type { Link } from '@setvi/shared/interfaces';
import type {
  PreviewEmailClient,
  CreatePreviewTestResponse,
  PreviewTestResultResponse,
  ApiResultsResponse,
  PreviewScreenshot,
  ApiResponse,
  CreatePreviewData
} from './types';

const getApiBaseUrl = (): string => process.env.ADMIN_API_BASE_URL || '';

const getAuthHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.token}`,
  'Content-Type': 'application/json'
});

const apiFetch = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const baseUrl = getApiBaseUrl();

  if (!baseUrl) {
    throw new Error('ADMIN_API_BASE_URL not configured.');
  }

  const response = await fetch(`${baseUrl}${endpoint}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Email Preview API error (${response.status}): ${errorText || response.statusText}`
    );
  }

  return response.json();
};

export const listEmailClients = async (): Promise<PreviewEmailClient[]> => {
  const result = await apiFetch<{
    clients: Record<string, PreviewEmailClient[]>;
  }>('/api/v2.0/email/preview/clients');

  if (result.clients && typeof result.clients === 'object') {
    return Object.values(result.clients).flat();
  }

  return [];
};

const buildLinkHrefMap = (links?: Link[]): Record<string, string> => {
  if (!links?.length) return {};
  const map: Record<string, string> = {};
  links.forEach(link => {
    if (!link.Placeholder) return;
    try {
      map[link.Placeholder] = generateLinkViewerUrl(getPreviewLink(link));
    } catch {
      map[link.Placeholder] = 'https://example.com';
    }
  });
  return map;
};

const cleanGuidPlaceholders = (body: string, links?: Link[]): string => {
  const guidPattern =
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
  const guidTextPattern =
    /\[?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\]?/gi;
  const hrefMap = buildLinkHrefMap(links);
  const div = document.createElement('div');
  div.innerHTML = body;

  Array.from(div.querySelectorAll('a')).forEach(anchor => {
    const guidMatch = anchor.id?.match(guidPattern);
    const placeholder = guidMatch?.[0];
    const viewerUrl = placeholder ? hrefMap[placeholder] : null;

    if (guidPattern.test(anchor.href)) {
      // eslint-disable-next-line no-param-reassign
      anchor.href = viewerUrl || 'https://example.com';
    }
    if (anchor.id && guidPattern.test(anchor.id)) {
      anchor.removeAttribute('id');
    }
    // eslint-disable-next-line no-param-reassign
    anchor.innerHTML = anchor.innerHTML.replace(guidTextPattern, '');
  });

  return div.innerHTML;
};

export const createPreviewTest = async (
  subject: string,
  html: string,
  links?: Link[]
): Promise<CreatePreviewTestResponse> => {
  const cleanedHtml = cleanGuidPlaceholders(html, links);
  const response = await apiFetch<ApiResponse<CreatePreviewData>>(
    '/api/v2.0/email/preview',
    {
      method: 'POST',
      body: JSON.stringify({ subject, htmlBody: cleanedHtml })
    }
  );

  if (response.Status !== 'Success' || !response.Data) {
    throw new Error(response.Error || 'Failed to create preview test');
  }

  if (!response.Data.id) {
    throw new Error('Invalid API response: missing preview test ID');
  }

  return {
    id: response.Data.id,
    reference_id: response.Data.customer_id
  };
};

export const getPreviewResults = async (
  testId: string
): Promise<ApiResultsResponse> => {
  const response = await apiFetch<ApiResponse<ApiResultsResponse>>(
    '/api/v2.0/email/preview/results',
    {
      method: 'POST',
      body: JSON.stringify({ id: testId })
    }
  );

  if (response.Status !== 'Success' || !response.Data) {
    throw new Error(response.Error || 'Failed to get preview results');
  }

  return response.Data;
};

const normalizeStatus = (
  apiStatus: string
): 'completed' | 'processing' | 'failed' => {
  switch (apiStatus) {
    case 'Complete':
      return 'completed';
    case 'Processing':
    case 'Pending':
      return 'processing';
    case 'Bounced':
      return 'failed';
    default:
      return 'processing';
  }
};

export const DEFAULT_EMAIL_CLIENTS = [
  'iphone16_18',
  'android14_gmailapp_pixel8_lm',
  'outlook19',
  'applemail16',
  'm365_w11_lm_dt',
  'gmailcom-lm_chrcurrent_win10',
  'outlookcom-lm_chrcurrent_win10',
  'yahoocom-lm_chrcurrent_win10'
];

export const DEFAULT_CLIENT_META: Record<
  string,
  { name: string; os: string; category: string }
> = {
  iphone16_18: { name: 'iPhone 16', os: 'iOS 18', category: 'Mobile' },
  android14_gmailapp_pixel8_lm: {
    name: 'Gmail App Pixel 8',
    os: 'Android 14',
    category: 'Mobile'
  },
  outlook19: { name: 'Outlook 2019', os: 'Windows 10', category: 'Desktop' },
  applemail16: {
    name: 'Apple Mail 16',
    os: 'macOS 13',
    category: 'Desktop'
  },
  m365_w11_lm_dt: {
    name: 'Outlook Microsoft 365',
    os: 'Windows 11',
    category: 'Desktop'
  },
  'gmailcom-lm_chrcurrent_win10': {
    name: 'Gmail.com Chrome',
    os: 'Windows 10',
    category: 'Web'
  },
  'outlookcom-lm_chrcurrent_win10': {
    name: 'Outlook.com Chrome',
    os: 'Windows 10',
    category: 'Web'
  },
  'yahoocom-lm_chrcurrent_win10': {
    name: 'Yahoo.com Chrome',
    os: 'Windows 10',
    category: 'Web'
  }
};

export const normalizePreviewResults = (
  testId: string,
  results: ApiResultsResponse,
  expectedClientCount: number
): PreviewTestResultResponse => {
  const screenshots: PreviewScreenshot[] = [];
  let completedCount = 0;
  let processingCount = 0;
  let failedCount = 0;

  const returnedClientIds = new Set<string>();

  Object.entries(results).forEach(([clientId, result]) => {
    returnedClientIds.add(clientId);
    const status = normalizeStatus(result.status);

    if (status === 'completed') completedCount += 1;
    else if (status === 'failed') failedCount += 1;
    else processingCount += 1;

    const meta = DEFAULT_CLIENT_META[clientId];
    const screenshot: PreviewScreenshot = {
      id: clientId,
      display_name:
        result.display_name || meta?.name || result.client || clientId,
      client: result.client,
      os: result.os || meta?.os || 'Unknown',
      category: result.category || meta?.category || 'Unknown',
      status
    };

    if (status === 'completed') {
      screenshot.full_url =
        result.screenshots?.default || result.full_thumbnail || '';
      screenshot.thumbnail_url =
        result.thumbnail || result.full_thumbnail || '';
    }

    if (status === 'failed' && result.status_details) {
      screenshot.error =
        result.status_details.bounce_message ||
        result.status_details.bounce_code ||
        'Preview generation failed';
    }

    screenshots.push(screenshot);
  });

  // Ensure all default clients appear even if the API didn't return them
  DEFAULT_EMAIL_CLIENTS.forEach(clientId => {
    if (returnedClientIds.has(clientId)) return;

    const meta = DEFAULT_CLIENT_META[clientId];
    processingCount += 1;
    screenshots.push({
      id: clientId,
      display_name: meta?.name || clientId,
      client: clientId,
      os: meta?.os || 'Unknown',
      category: meta?.category || 'Unknown',
      status: 'processing'
    });
  });

  const doneCount = completedCount + failedCount;
  const totalCount = expectedClientCount || screenshots.length;
  const progress =
    totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  let overallStatus: 'completed' | 'processing' | 'failed';
  if (processingCount > 0 || doneCount < expectedClientCount) {
    overallStatus = 'processing';
  } else if (failedCount > 0 && completedCount === 0) {
    overallStatus = 'failed';
  } else {
    overallStatus = 'completed';
  }

  return {
    id: testId,
    status: overallStatus,
    progress,
    screenshots
  };
};

export * from './types';
